import { useMemo, useState } from "react";
import Link from "next/link";
import useTranslation from "next-translate/useTranslation";
import PureInput from "@/components/common/form/PureInput";
import useTariffs from "@/common/hook/dashboard/useTariffs";
import PureSelect from "@/components/common/form/PureSelect";
import InputRadio from "@/components/common/form/InputRadio";

const TARIFFS = Array.from({ length: 20 }, (v, i) => (i + 1) * 10);

const FindSalary = () => {
  const { t } = useTranslation("become-consultant");
  const minuteTariffs = useTariffs();
  const [minutePerWeek, setMinutePerWeek] = useState(30);
  const [pricePer, setPricePer] = useState(1.99 / 1.19);
  const [multiple, setMultiple] = useState("1");

  const tariffs = useMemo(() => {
    if (multiple === "1") {
      return minuteTariffs?.map((tariff) => tariff.centsPerMinute / 100) || [];
    }

    return TARIFFS;
  }, [multiple, minuteTariffs]);

  /* mt-16 and sm:mt-24 for default main margin. Because removed margin bottom */
  return (
    <section className="mt-16 bg-purple-800 py-24 sm:mt-24">
      <div className="mx-auto flex w-full flex-col items-center px-4 lg:box-content lg:w-[826px] lg:px-8">
        <div className="flex h-16 w-full rounded-full bg-white font-semibold">
          <div className="flex-center h-full w-[70%] rounded-full bg-blue text-white">
            <span className="text-xl">{t`find-salary.for-you`}</span>
            <span className="ml-3 text-3xl">70%</span>
          </div>
          <div className="flex-center grow text-grey-700">
            <span className="text-xs lg:text-base">{t`find-salary.for-us`}</span>
            <span className="ml-1 text-xs lg:ml-3 lg:text-xl">30%</span>
          </div>
        </div>
        <p className="mt-6 text-center text-xs text-grey-700 lg:text-sm">{t`find-salary.subtext`}</p>

        <h2 className="mt-16 text-center font-semibold lg:text-xl">{t`find-salary.title`}</h2>
        <div className="mt-6 grid w-full grid-cols-1 items-start justify-items-center gap-x-20 gap-y-8 rounded-lg border border-purple py-6 px-16 text-center font-semibold lg:grid-flow-col lg:grid-cols-3 lg:grid-rows-[1fr_auto] lg:py-9 lg:px-24">
          <label className="w-full text-grey-800" htmlFor="minutesOfConversation">
            {t`find-salary.minutes-of-conversation`}
          </label>
          <PureInput
            register={() => ({
              onChange: ({ target }) => setMinutePerWeek(target.value.replace(/\D/, "")),
              value: minutePerWeek.toString(),
            })}
            id="minutesOfConversation"
            className="w-full"
            type="number"
            min={1}
          />

          <div className="w-full">
            <label className="text-grey-800" htmlFor="tariff">
              {t`find-salary.price-per.title`}
            </label>
            <div className="mt-3 flex justify-center gap-3 text-xs">
              <InputRadio
                id="minute"
                register={() => ({
                  name: "calculateType",
                  onChange: ({ target }) => {
                    setMultiple(target.value);
                    setPricePer(0.99 / 1.19);
                  },
                  checked: multiple === "1",
                })}
                value="1"
                label={t`find-salary.price-per.minute`}
              />
              <InputRadio
                id="hour"
                register={() => ({
                  name: "calculateType",
                  onChange: ({ target }) => {
                    setMultiple(target.value);
                    setPricePer(10 / 1.19);
                  },
                  checked: multiple === "60",
                })}
                label={t`find-salary.price-per.hour`}
                value="60"
              />
            </div>
          </div>
          <PureSelect
            className="h-11 w-full"
            register={() => ({
              onChange: ({ target }) => setPricePer(target.value),
              value: pricePer,
            })}
            id="tariff"
            fit
          >
            {tariffs.map((tariff) => (
              <option key={tariff} value={tariff / 1.19}>
                {tariff} &euro;
              </option>
            ))}
          </PureSelect>

          <h3 className="text-grey-800">{t`find-salary.your-salary`}</h3>
          <p className="text-3xl text-blue">
            {(((minutePerWeek * 4 * pricePer) / multiple) * (70 / 100)).toFixed(2)} €
          </p>
        </div>

        <p className="mt-6 px-9 text-center text-sm text-grey-800 lg:px-36 lg:text-xl">{t`find-salary.part-of-vialantis`}</p>

        <Link href="/register/start" passHref>
          <button type="button" className="button-lg hover-orange mt-12 lg:mt-16">
            {t`start-now`}
          </button>
        </Link>
      </div>
    </section>
  );
};

export default FindSalary;
